<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue'

interface PreStopAmountItem {
  timestamp: string | Date
  preSetAmount1: number
  preSetAmount2: number
  preSetAmount3: number
  wantedMaxCement: number
}

interface UniquePumpStatusItem {
  timestamp: string | Date
  pumpId: number
  endPressureBar: number
  endPressureSec: number
}

interface RecipeItem {
  timestamp: string | Date
  recipeId: number
  recipeName: string
  cement1: number
  cement2: number
  water: number
  silicaSlurry: number
  additive1: number
  additive2: number
  mixingTime: number
  amount: number
  vcValue: number
  strength: number
}

const props = defineProps({
  recipeData: {
    type: Array as PropType<RecipeItem[]>,
    required: true,
    default: () => []
  },           
  presetAmounts: {
    type: Array as PropType<PreStopAmountItem[]>,
    required: true,
    default: () => []
  },
  uniquePumpsStatus: {
    type: Array as PropType<UniquePumpStatusItem[]>,
    required: false,
    default: () => []
  }       
});

const formatDate = (date: any) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const pumpHeaders = [
  { title: 'Effectivity Date', key: 'timestamp' },
  { title: 'Equipment', key: 'pumpId' },
  { title: 'End Pressure', key: 'endPressureBar', align: 'end' as const },
  { title: 'Hold Time', key: 'endPressureSec', align: 'end' as const },
];

const showLatestPumpsOnly = ref(true);

const filteredPumpStatus = computed(() => {
  const data = props.uniquePumpsStatus || [];
  if (!showLatestPumpsOnly.value) return data;

  const latest = new Map<number, UniquePumpStatusItem>();
  data.forEach(item => {
    const existing = latest.get(item.pumpId);
    if (!existing || new Date(item.timestamp) > new Date(existing.timestamp)) {
      latest.set(item.pumpId, item);
    }
  });

  return Array.from(latest.values()).sort((a, b) => a.pumpId - b.pumpId);
});
</script>

<template>
  <v-container fluid>
    <v-alert
      v-if="!presetAmounts || presetAmounts.length === 0"
      type="info"
      variant="tonal"
      class="mb-4"
      icon="mdi-clipboard-text-outline"
    >
      No Setup data available in current log.
    </v-alert>

    <template v-else>
      <!-- Preset Amounts -->
      <v-card class="mb-6" elevation="2">
        <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
          Preset Amounts
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div v-for="(preset, index) in presetAmounts" :key="'preset-'+index" class="mb-6">
            <div class="text-subtitle-2 text-medium-emphasis mb-2 font-weight-bold font-monospace">
              {{ formatDate(preset.timestamp) }}
            </div>
            <v-row dense>
              <v-col cols="6" sm="3">
                <v-sheet color="blue-lighten-5" class="pa-3 rounded text-center border border-blue-lighten-4">
                  <div class="text-caption text-uppercase font-weight-bold text-blue-darken-2 opacity-80">Pre-Stop 1</div>
                  <div class="text-h5 font-weight-bold text-blue-darken-4">{{ preset.preSetAmount1 }}<span class="text-caption font-weight-bold ml-1">L</span></div>
                </v-sheet>
              </v-col>
              <v-col cols="6" sm="3">
                <v-sheet color="blue-lighten-5" class="pa-3 rounded text-center border border-blue-lighten-4">
                  <div class="text-caption text-uppercase font-weight-bold text-blue-darken-2 opacity-80">Pre-Stop 2</div>
                  <div class="text-h5 font-weight-bold text-blue-darken-4">{{ preset.preSetAmount2 }}<span class="text-caption font-weight-bold ml-1">L</span></div>
                </v-sheet>
              </v-col>
              <v-col cols="6" sm="3">
                <v-sheet color="blue-lighten-5" class="pa-3 rounded text-center border border-blue-lighten-4">
                  <div class="text-caption text-uppercase font-weight-bold text-blue-darken-2 opacity-80">Pre-Stop 3</div>
                  <div class="text-h5 font-weight-bold text-blue-darken-4">{{ preset.preSetAmount3 }}<span class="text-caption font-weight-bold ml-1">L</span></div>
                </v-sheet>
              </v-col>
              <v-col cols="6" sm="3">
                <v-sheet color="green-lighten-5" class="pa-3 rounded text-center border border-green-lighten-4">
                  <div class="text-caption text-uppercase font-weight-bold text-green-darken-2 opacity-80">Max Cement</div>
                  <div class="text-h5 font-weight-bold text-green-darken-4">{{ preset.wantedMaxCement }}<span class="text-caption font-weight-bold ml-1">kg</span></div>
                </v-sheet>
              </v-col>
            </v-row>
            <v-divider v-if="index < presetAmounts.length - 1" class="mt-6"></v-divider>
          </div>
        </v-card-text>
      </v-card>

      <!-- Pump Criteria Changes -->
      <v-card class="mb-6" elevation="2" v-if="uniquePumpsStatus && uniquePumpsStatus.length > 0">
        <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
          <span class="text-subtitle-1 font-weight-bold">Pump Criteria Changes</span>
          <v-switch
            v-model="showLatestPumpsOnly"
            label="Show Latest Registered per Pump"
            color="primary"
            density="compact"
            hide-details
            class="ml-4"
          ></v-switch>
        </v-card-title>
        <v-divider></v-divider>
        <v-data-table
          :headers="pumpHeaders"
          :items="filteredPumpStatus"
          density="compact"
          class="text-body-2"
          hover
        >
          <template v-slot:item.timestamp="{ item }">
            <span class="text-medium-emphasis font-monospace">{{ formatDate(item.timestamp) }}</span>
          </template>
          <template v-slot:item.pumpId="{ item }">
            <v-chip size="small" color="light-blue-darken-3" variant="tonal" class="font-weight-bold">
              Pump {{ item.pumpId }}
            </v-chip>
          </template>
          <template v-slot:item.endPressureBar="{ item }">
            {{ item.endPressureBar }} <span class="text-caption text-medium-emphasis ml-1">Bar</span>
          </template>
          <template v-slot:item.endPressureSec="{ item }">
            {{ item.endPressureSec }} <span class="text-caption text-medium-emphasis ml-1">Sec</span>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- Active Recipes -->
    <v-card elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Active Recipes
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col v-for="(recipe, index) in recipeData" :key="'recipe-' + index" cols="12" sm="6" md="4" lg="3">
            <v-card variant="outlined" class="h-100">
              <v-card-title class="bg-blue-grey-darken-3 text-white py-2 px-3 d-flex align-center text-subtitle-2">
                <v-chip size="x-small" color="white" variant="flat" class="mr-2 text-blue-grey-darken-4 font-weight-bold">
                  #{{ recipe.recipeId }}
                </v-chip>
                <span class="text-truncate">{{ recipe.recipeName || 'Unnamed Recipe' }}</span>
              </v-card-title>
              <v-card-text class="pa-3">
                <div class="d-flex justify-space-between bg-grey-lighten-5 pa-2 rounded mb-3 text-caption">
                  <div class="font-weight-bold">VC: <span class="text-medium-emphasis">{{ recipe.vcValue }}</span></div>
                  <div class="font-weight-bold">Vol: <span class="text-medium-emphasis">{{ recipe.amount }}L</span></div>
                  <div class="font-weight-bold">Mix: <span class="text-medium-emphasis">{{ recipe.mixingTime }}s</span></div>
                </div>
                <v-row dense class="text-caption">
                  <v-col cols="6">
                    <div class="text-uppercase text-medium-emphasis font-weight-bold" style="font-size: 0.7rem;">Cement 1/2</div>
                    <div class="font-weight-bold">{{ recipe.cement1 }} / {{ recipe.cement2 }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-uppercase text-medium-emphasis font-weight-bold" style="font-size: 0.7rem;">Water</div>
                    <div class="font-weight-bold">{{ recipe.water }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-uppercase text-medium-emphasis font-weight-bold" style="font-size: 0.7rem;">Silica</div>
                    <div class="font-weight-bold">{{ recipe.silicaSlurry }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-uppercase text-medium-emphasis font-weight-bold" style="font-size: 0.7rem;">Additives</div>
                    <div class="font-weight-bold">{{ recipe.additive1 }} | {{ recipe.additive2 }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>
<style scoped>
/* Utility class for monospace font */
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>
